import React from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Box,
  Button,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import PaginationData from 'types/models/PaginationData';
import 'styles/pages/ConsumptionsReportPage.scss';
import Consumer from 'types/models/Consumer';
import EventConsumptionsReportForm from 'forms/EventConsumptionsReportForm';
import { EventConsumptionReportPayloadData } from 'types/forms/EventConsumptionsReportFormProps';
import { downloadEventConsumptionsReport, getEventConsumptionsReport } from 'services/eventServices';

export default (): JSX.Element => {
  const { t } = useTranslation();

  const [currentFormData, setCurrentFormData] = React.useState<EventConsumptionReportPayloadData | null>(null);
  const [total, setTotal] = React.useState<number | null>(null);
  const [paginationConsumers, setPaginationConsumers] = React.useState<PaginationData<Consumer> | null>(null);
  const paymentIdsForSearch = React.useRef<string[]>([]);

  const loadData = async (page?: number) => {
    if (!currentFormData) return;
    const { ...consumptionsReportPayload } = currentFormData;
    const consumers = await getEventConsumptionsReport({ ...consumptionsReportPayload }, page);
    if (consumers) {
      setPaginationConsumers(consumers as PaginationData<Consumer>);
    }
  };

  const downloadReport = async (): Promise<void> => {
    if (!currentFormData) return;
    const result = await downloadEventConsumptionsReport({ ...currentFormData, download: true });
    if (result) {
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(
        new Blob([result as Blob], {
          type: 'application/octet-stream',
        }),
      );
      a.download = 'reporte_consumidores.xlsx';
      a.click();
    }
  };

  React.useEffect(() => {
    if (total && paginationConsumers) {
      setTotal(null);
      setPaginationConsumers(null);
    }
    if (paymentIdsForSearch.current.length > 0) {
      paymentIdsForSearch.current = [];
    }
    loadData();
  }, [currentFormData]);

  const renderContent = (consumer: Consumer, index: number): JSX.Element => {
    return (
      <React.Fragment key={index}>
        <TableRow className={'content-table'}>
          <TableCell>{consumer.documentNumber}</TableCell>
          <TableCell>{consumer.name}</TableCell>
          <TableCell>{consumer.lastName}</TableCell>
          <TableCell>{consumer.eventName}</TableCell>
          <TableCell>{consumer.consumedCount}</TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <>
      <EventConsumptionsReportForm currentFormData={currentFormData} setCurrentFormData={setCurrentFormData} />
      <>
        <TableContainer className="table-container" component={Paper}>
          <Table>
            <TableHead className="table-header">
              <TableRow>
                <TableCell>{t('document-number')}</TableCell>
                <TableCell>{t('name')}</TableCell>
                <TableCell>{t('last-name')}</TableCell>
                <TableCell>{t('event-name')}</TableCell>
                <TableCell>{t('consumed-amount')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginationConsumers?.data && paginationConsumers?.data.length > 0 ? (
                paginationConsumers?.data.map((consumer: Consumer, index) => renderContent(consumer, index))
              ) : (
                <TableRow>
                  <TableCell>{t('event-consumptions-no-operations')}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {paginationConsumers && (
          <>
            <TablePagination
              component="div"
              count={paginationConsumers.total}
              page={paginationConsumers.currentPage - 1}
              onPageChange={(_, page: number) => {
                loadData(page + 1);
              }}
              rowsPerPage={paginationConsumers.perPage}
              rowsPerPageOptions={[]}
              labelDisplayedRows={({ from, to, count }) => {
                return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
              }}
            />
            {paginationConsumers?.data.length > 0 && (
              <Box marginTop="1rem">
                <Button
                  className="btn-50"
                  variant="contained"
                  color="primary"
                  type="submit"
                  onClick={() => {
                    downloadReport();
                  }}
                >
                  {t('event-consumptions-download')}
                </Button>
              </Box>
            )}
          </>
        )}
      </>
    </>
  );
};
